/**
 * Chart Utilities for Free Tier Limitations
 * 
 * Provides utilities to truncate chart data beyond 5 years for free users
 * and display upgrade prompts
 */

/**
 * Truncates projection data to specified number of years
 * @param {Array} data - Array of data points with year property
 * @param {number} startYear - The starting year of projections
 * @param {number} maxYears - Maximum number of years to show (5 for free tier)
 * @param {boolean} shouldTruncate - Whether to truncate (false for subscribed users)
 * @returns {Object} { truncatedData, isTruncated, truncatedAtYear }
 */
export const truncateProjectionData = (data, startYear, maxYears = 5, shouldTruncate = true) => {
  if (!data || !Array.isArray(data)) {
    return { truncatedData: [], isTruncated: false, truncatedAtYear: null };
  }

  if (!shouldTruncate) {
    return { truncatedData: data, isTruncated: false, truncatedAtYear: null };
  }

  const cutoffYear = startYear + maxYears;
  const truncatedData = data.filter(item => item.year <= cutoffYear);
  const isTruncated = data.length > truncatedData.length;

  return {
    truncatedData,
    isTruncated,
    truncatedAtYear: cutoffYear,
    totalYears: data.length,
    visibleYears: truncatedData.length
  };
};

/**
 * Truncates time series data (arrays of {year, value} objects)
 * @param {Array} seriesData - Array of series objects with data arrays
 * @param {number} startYear - The starting year of projections
 * @param {number} maxYears - Maximum number of years to show
 * @param {boolean} shouldTruncate - Whether to truncate
 * @returns {Object} { truncatedSeries, isTruncated, truncatedAtYear }
 */
export const truncateSeriesData = (seriesData, startYear, maxYears = 5, shouldTruncate = true) => {
  if (!seriesData || !Array.isArray(seriesData)) {
    return { truncatedSeries: [], isTruncated: false, truncatedAtYear: null };
  }

  if (!shouldTruncate) {
    return { truncatedSeries: seriesData, isTruncated: false, truncatedAtYear: null };
  }

  const cutoffYear = startYear + maxYears;
  let isTruncated = false;

  const truncatedSeries = seriesData.map(series => {
    if (!series.data || !Array.isArray(series.data)) {
      return series;
    }

    const truncatedData = series.data.filter(item => item.year <= cutoffYear);
    
    if (series.data.length > truncatedData.length) {
      isTruncated = true;
    }

    return {
      ...series,
      data: truncatedData
    };
  });

  return {
    truncatedSeries,
    isTruncated,
    truncatedAtYear: cutoffYear
  };
};

/**
 * Gets the appropriate message for truncated data
 * @param {number} visibleYears - Number of years visible
 * @param {number} totalYears - Total number of years available
 * @returns {string} Message to display
 */
export const getTruncationMessage = (visibleYears, totalYears) => {
  const hiddenYears = totalYears - visibleYears;
  return `Showing ${visibleYears} of ${totalYears} years. ${hiddenYears} year${hiddenYears !== 1 ? 's' : ''} hidden. Upgrade to see full projections.`;
};

/**
 * Gets the truncation config for a specific chart
 * @param {string} chartName - Name of the chart
 * @param {boolean} isSubscribed - Whether user is subscribed
 * @returns {Object} { shouldTruncate, maxYears, showUpgradePrompt }
 */
/**
 * Gets the truncation config for a specific chart
 * @param {string} chartName - Name of the chart
 * @param {boolean} isSubscribed - Whether user is subscribed
 * @param {boolean} isDemoMode - Whether demo mode is active
 * @returns {Object} { shouldTruncate, maxYears, showUpgradePrompt }
 */
export const getChartTruncationConfig = (chartName, isSubscribed, isDemoMode = false) => {
  // Net Worth Trajectory should never be truncated (per user requirement)
  const neverTruncateCharts = ['NetWorthTrajectory', 'net-worth'];
  
  const shouldNeverTruncate = neverTruncateCharts.some(name => 
    chartName.toLowerCase().includes(name.toLowerCase())
  );

  // Demo mode: Never truncate (backend sends realistic projection)
  // Subscribed: Never truncate (full access)
  // Free tier: Truncate to 5 years (unless in never-truncate list)
  const shouldTruncate = !isSubscribed && !isDemoMode && !shouldNeverTruncate;
  const showUpgradePrompt = !isSubscribed && !isDemoMode && !shouldNeverTruncate;

  return {
    shouldTruncate,
    maxYears: 5,
    showUpgradePrompt
  };
};

/**
 * Adds visual indicators for truncated data points
 * @param {Array} data - Chart data
 * @param {number} cutoffYear - Year at which data is truncated
 * @returns {Array} Data with truncation indicators
 */
export const addTruncationIndicators = (data, cutoffYear) => {
  if (!data || !cutoffYear) return data;

  return data.map(item => ({
    ...item,
    isCutoff: item.year === cutoffYear,
    isTruncated: item.year > cutoffYear
  }));
};

/**
 * Calculate chart dimensions considering upgrade prompt space
 * @param {boolean} showUpgradePrompt - Whether upgrade prompt will be shown
 * @param {number} baseHeight - Base chart height
 * @returns {number} Adjusted height
 */
export const getAdjustedChartHeight = (showUpgradePrompt, baseHeight = 400) => {
  // Reserve space for upgrade prompt if needed
  return showUpgradePrompt ? baseHeight - 40 : baseHeight;
};
